import React, { useState, useRef, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  CardMedia, 
  Box, 
  Chip,
  IconButton,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Translate, 
  VolumeUp, 
  Mic, 
  Chat, 
  Analytics, 
  Business,
  ArrowForward,
  Star,
  PlayCircle
} from '@mui/icons-material';

const ProductCard = ({ title, img, description, benefits, useCases, link }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Intersection Observer for animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Icon mapping for different products
  const getProductIcon = (title) => {
    if (title.includes('Avoices') || title.includes('Translation')) return <Translate />;
    if (title.includes('Voice') || title.includes('Pod')) return <VolumeUp />;
    if (title.includes('Agents') || title.includes('Chatbot')) return <Chat />;
    if (title.includes('Agriculture')) return <Business />;
    if (title.includes('Cortex') || title.includes('Analytics') || title.includes('AfriData') || title.includes('Pulse')) return <Analytics />;
    if (title.includes('OCR')) return <Business />;
    if (title.includes('CarryiT')) return <Business />;
    if (title.includes('Rentals')) return <Business />;
    return <Translate />;
  };

  // Get product category
  const getProductCategory = (title) => {
    if (title.includes('Avoices') || title.includes('Translation')) return 'Language AI';
    if (title.includes('Voice') || title.includes('Pod')) return 'Voice Technology';
    if (title.includes('Agents') || title.includes('Chatbot')) return 'AI Agents';
    if (title.includes('Agriculture')) return 'Agricultural AI';
    if (title.includes('Cortex') || title.includes('Analytics') || title.includes('AfriData') || title.includes('Pulse')) return 'Data & Intelligence';
    if (title.includes('OCR')) return 'Data Processing';
    if (title.includes('CarryiT')) return 'Logistics & Supply Chain';
    if (title.includes('Rentals')) return 'Real Estate';
    return 'AI Products';
  };

  // Get product color
  const getProductColor = (title) => {
    if (title.includes('Avoices') || title.includes('Translation')) return '#F59E0B';
    if (title.includes('Voice') || title.includes('Pod')) return '#F59E0B';
    if (title.includes('Agents') || title.includes('Chatbot')) return '#F59E0B';
    if (title.includes('Agriculture')) return '#F59E0B';
    if (title.includes('Cortex') || title.includes('Analytics')) return '#F59E0B';
    if (title.includes('OCR')) return '#F59E0B';
    return '#F59E0B';
  };

  return (
    <Card 
      ref={cardRef}
      sx={{
        height: '100%',
        borderRadius: '20px',
        overflow: 'hidden',
        background: 'white',
        border: '1px solid rgba(245,158,11, 0.08)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${getProductColor(title)} 0%, ${getProductColor(title)}80 100%)`,
          transform: 'scaleX(0)',
          transition: 'transform 0.4s ease',
          zIndex: 2,
        },
        '&:hover': {
          transform: 'translateY(-12px)',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.12)',
          borderColor: `${getProductColor(title)}40`,
          '&::before': {
            transform: 'scaleX(1)',
          },
          '& .product-image': {
            transform: 'scale(1.05)',
          },
          '& .product-overlay': {
            opacity: 1,
          },
          '& .product-icon': {
            transform: 'scale(1.1) rotate(5deg)',
          },
          '& .product-arrow': {
            transform: 'translateX(6px)',
          }
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Section */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        <CardMedia
          component="img"
          image={img}
          alt={title}
          className="product-image"
          sx={{ 
            height: 240,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
        
        {/* Gradient Overlay */}
        <Box 
          className="product-overlay"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${getProductColor(title)}20 0%, transparent 100%)`,
            opacity: 0,
            transition: 'opacity 0.4s ease',
            zIndex: 1,
          }}
        />
        
        {/* Category Badge */}
        <Box sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 3,
        }}>
          <Chip 
            label={getProductCategory(title)}
            size="small"
            sx={{ 
              backgroundColor: `${getProductColor(title)}`,
              color: 'white',
              fontWeight: 700,
              fontSize: '0.7rem',
              height: 26,
              px: 1.5,
              '& .MuiChip-label': {
                px: 1,
              }
            }} 
          />
        </Box>

        {/* Floating Icon */}
        <Box 
          className="product-icon"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 3,
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            color: getProductColor(title),
            fontSize: '24px',
          }}
        >
          {getProductIcon(title)}
        </Box>

        {/* Play Button Overlay */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
          opacity: isHovered ? 1 : 0,
          transition: 'all 0.3s ease',
        }}>
          <IconButton
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: getProductColor(title),
              width: 56,
              height: 56,
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
              '&:hover': {
                backgroundColor: 'white',
                transform: 'scale(1.1)',
              }
            }}
          >
            <PlayCircle sx={{ fontSize: 32 }} />
          </IconButton>
        </Box>
      </Box>
      
      {/* Content Section */}
      <CardContent sx={{ 
        p: 4, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
      }}>
        {/* Title */}
        <Typography 
          variant="h5" 
          sx={{ 
            color: '#111827', 
            fontWeight: 800,
            fontSize: '1.25rem',
            lineHeight: 1.3,
            mb: 2,
            minHeight: '3.25rem',
          }}
        >
          {title}
        </Typography>
        
        {/* Description */}
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#6B7280',
            lineHeight: 1.7,
            fontSize: '1rem',
            flex: 1,
            mb: 3,
            fontWeight: 500,
          }}
        >
          {description}
        </Typography>

        {/* Benefits Section */}
        {benefits && (
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 700,
                color: '#111827',
                mb: 2,
                fontSize: '0.9rem',
              }}
            >
              Key Benefits:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {benefits.map((benefit, index) => (
                <Chip
                  key={index}
                  label={benefit}
                  size="small"
                  sx={{
                    backgroundColor: `${getProductColor(title)}15`,
                    color: getProductColor(title),
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 24,
                    px: 1,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Use Cases Section */}
        {useCases && (
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 700,
                color: '#111827',
                mb: 2,
                fontSize: '0.9rem',
              }}
            >
              Use Cases:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {useCases.map((useCase, index) => (
                <Chip
                  key={index}
                  label={useCase}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(107, 114, 128, 0.1)',
                    color: '#6B7280',
                    fontWeight: 500,
                    fontSize: '0.7rem',
                    height: 22,
                    px: 1,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
        
        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: 2,
          mt: 'auto',
        }}>
          {link && (
            <Button
              variant="contained"
              size="small"
              endIcon={<ArrowForward />}
              sx={{
                background: '#111827',
                color: 'white',
                px: 3,
                py: 1.5,
                borderRadius: '20px',
                fontWeight: 700,
                fontSize: '0.85rem',
                textTransform: 'none',
                boxShadow: `0 8px 25px rgba(0,0,0,0.1)`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 15px 35px rgba(0,0,0,0.2)`,
                  background: '#1F2937',
                }
              }}
              onClick={() => window.open(link, '_blank')}
            >
              Visit Platform
            </Button>
          )}
          <Button
            variant={link ? "outlined" : "contained"}
            size="small"
            sx={{
              background: link ? 'transparent' : `linear-gradient(135deg, ${getProductColor(title)} 0%, ${getProductColor(title)}80 100%)`,
              color: link ? getProductColor(title) : 'white',
              border: link ? `2px solid ${getProductColor(title)}` : 'none',
              px: 3,
              py: 1.5,
              borderRadius: '20px',
              fontWeight: 700,
              fontSize: '0.85rem',
              textTransform: 'none',
              boxShadow: link ? 'none' : `0 8px 25px ${getProductColor(title)}30`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: link ? `0 8px 20px ${getProductColor(title)}20` : `0 15px 35px ${getProductColor(title)}40`,
                backgroundColor: link ? `${getProductColor(title)}10` : undefined,
              }
            }}
            onClick={() => window.open('mailto:phosaico@gmail.com?subject=Schedule Demo - ' + title, '_blank')}
          >
            Schedule Demo
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProductCard;